import { useEffect, useState } from "react";
import { toast } from "sonner";
import { PackageCheck } from "lucide-react";
import { api, errMsg } from "@/lib/api";

export default function AdminOutstanding() {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState("");

  const load = () =>
    api.get("/admin/outstanding").then((r) => setRows(r.data.outstanding)).catch(() => setRows([]));

  useEffect(() => { load(); }, []);

  const markDelivered = async (row) => {
    setBusy(row.order_id + row.product_id);
    try {
      await api.patch(`/admin/orders/${row.order_id}/outstanding`, {
        items: [{ product_id: row.product_id, outstanding: 0 }],
      });
      toast.success(`${row.name} marked as delivered for ${row.order_number}`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };

  return (
    <div data-testid="admin-outstanding">
      <h1 className="font-display text-2xl font-bold tracking-tight text-navy mb-1">Outstanding deliveries</h1>
      <p className="text-xs text-slate-400 mb-6">
        Items owed to customers after a partial delivery. Deliver these as soon as stock arrives —{" "}
        <span className="font-bold text-navy">delivery of outstanding items is always FREE</span>.
      </p>

      {!rows ? (
        <div className="h-32 bg-white rounded-2xl border border-slate-100 animate-pulse" />
      ) : rows.length === 0 ? (
        <p className="bg-white rounded-2xl border border-slate-200/80 p-10 text-center text-sm text-slate-500" data-testid="no-outstanding">
          Nothing outstanding — every order is fully delivered.
        </p>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => (
            <div key={r.order_id + r.product_id} className="bg-white rounded-2xl border border-amber-200 p-5 flex items-center gap-4 flex-wrap"
              data-testid={`outstanding-${r.order_number}-${r.sku || r.product_id.slice(0, 6)}`}>
              <div className="min-w-[110px]">
                <p className="font-mono font-bold text-navy text-sm">{r.order_number}</p>
                <p className="text-[11px] text-slate-400">{new Date(r.created_at).toLocaleDateString("en-ZA")}</p>
              </div>
              <div className="flex-1 min-w-[200px]">
                <p className="text-sm font-semibold text-navy">{r.name}</p>
                <p className="text-xs text-slate-500 font-mono">{r.sku}</p>
                <p className="text-xs text-slate-500 mt-0.5">
                  {r.customer.name} · {r.customer.phone || r.customer.email}
                  {r.delivery_method === "delivery" && r.address
                    ? ` · deliver to ${r.address.line1}, ${r.address.city}`
                    : " · collection"}
                </p>
              </div>
              <span className="text-[11px] font-bold px-3 py-1.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200" data-testid={`outstanding-qty-${r.order_number}`}>
                Owed: {r.outstanding_qty} of {r.qty_ordered}
              </span>
              <button
                onClick={() => markDelivered(r)}
                disabled={busy === r.order_id + r.product_id}
                className="h-9 px-4 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold flex items-center gap-1.5 disabled:opacity-50 transition-colors"
                data-testid={`outstanding-deliver-${r.order_number}`}
              >
                <PackageCheck size={14} /> {busy === r.order_id + r.product_id ? "Saving…" : "Mark delivered (free)"}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
